import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

@Component({
  selector: 'app-searchbar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
})
export class SearchbarComponent {
  @Input() placeholder: string;
  @Input() showSearchBar: boolean;
  @Input() disabled = false;
  @Output() ionChange = new EventEmitter<string>();
  @ViewChild('search') search: any;
  @Input() value = '';
  public clearButtonMode: 'always' | 'focus' = 'always';
  @Output() clear = new EventEmitter<void>();
  private clearButtonKeyHandlers = new WeakSet<HTMLButtonElement>();
  private inputKeyHandlers = new WeakSet<HTMLInputElement>();

  onSearchInput(event: any) {
    if (this.disabled) {
      return;
    }

    const value = event?.target?.value;
    this.enableClearIconKeyboardSupport();

    if (!value || value.trim() === '') {

      this.hideSearchBar(); // Hide when input is cleared
    }
    this.ionChange.emit(value);
  }
  // To display the searchbar
  public showSearchBarIcon() {
    if (this.disabled) {
      return;
    }

    this.showSearchBar = !this.showSearchBar;
    setTimeout(() => {
      this.search.setFocus();
      this.enableClearIconKeyboardSupport();
    }, 500);
  }

  // To hide the searchbar
  public hideSearchBar() {
    this.showSearchBar = false;
  }

  onSearchBlur(event: any) {
  const value = event?.target?.value;
  if (!value || value.trim() === '') {
    this.hideSearchBar(); // Only hide if input is empty
    this.ionChange.emit(value);
  }
}

  public onSearchFocus(): void {
    this.enableClearIconKeyboardSupport();
  }

  /**
   * Determines whether search clear on
   *
   * @param event
   */
 public onSearchClear() {
    if (this.disabled) {
      return;
    }

    this.hideSearchBar();
    this.clear.emit();
  }

  private enableClearIconKeyboardSupport(): void {
    setTimeout(() => {
      const searchInput = this.getSearchInputElement();
      const clearButton = this.getClearButtonElement();
      if (!searchInput || !clearButton) {
        return;
      }

      clearButton.setAttribute('tabindex', '0');
      clearButton.setAttribute('role', 'button');
      clearButton.setAttribute('aria-label', 'Clear search');

      if (this.clearButtonKeyHandlers.has(clearButton)) {
        // Keep going; input handler may still need registration.
      } else {
        clearButton.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          clearButton.click();
        }, true);

        clearButton.addEventListener('focus', () => {
          clearButton.classList.add('keyboard-focus-visible');
        }, true);

        clearButton.addEventListener('blur', () => {
          clearButton.classList.remove('keyboard-focus-visible');
        }, true);

        this.clearButtonKeyHandlers.add(clearButton);
      }

      if (this.inputKeyHandlers.has(searchInput)) {
        return;
      }

      searchInput.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key !== 'Tab' || event.shiftKey) {
          return;
        }

        const hasValue = !!searchInput.value?.trim();
        if (!hasValue) {
          return;
        }

        const currentClearButton = this.getClearButtonElement();
        if (!currentClearButton || !this.isClearButtonVisible(currentClearButton)) {
          return;
        }

        event.preventDefault();
        searchInput.blur();
        setTimeout(() => {
          currentClearButton.focus();
        }, 0);
      }, true);

      this.inputKeyHandlers.add(searchInput);
    }, 0);
  }

  private getSearchInputElement(): HTMLInputElement | null {
    const searchbarHost = this.search?.el as HTMLElement | undefined;
    const shadowRoot = searchbarHost?.shadowRoot;
    return shadowRoot?.querySelector('input.searchbar-input') as HTMLInputElement | null;
  }

  private getClearButtonElement(): HTMLButtonElement | null {
    const searchbarHost = this.search?.el as HTMLElement | undefined;
    const shadowRoot = searchbarHost?.shadowRoot;
    return shadowRoot?.querySelector('button.searchbar-clear-button') as HTMLButtonElement | null;
  }

  private isClearButtonVisible(clearButton: HTMLButtonElement): boolean {
    if (clearButton.classList.contains('searchbar-clear-button-hidden')) {
      return false;
    }

    const style = window.getComputedStyle(clearButton);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }
}
